/* Dedicated NIR signup step (SNDS appariement) — shown right after the
 * OTP-verified inscription step for roles with nir_collection_mode ≠ HIDDEN
 * (see helper/nir.ts shouldCollectNir, shared with the progress-bar spine).
 *
 * FIELDS: the NIR alone identifies its holder at the CNAM, so most roles get a
 * single field. When the role ALSO links the holder's own care
 * (nir_link_holder_care — e.g. Outborn's mother, whose own care is linked on top
 * of her children's), the step also asks the holder's date de naissance: the one
 * datum of the MIA triplet the NIR does not embed (sexe and birth year/month are
 * in the NIR). A child's sex/birth date always come from study data, never from
 * this page.
 *
 * LAYOUT: everything is normal document flow — title → intro card → inputs →
 * NextPrev pinned to the bottom by Container's flex column. Deliberately NOT
 * the absolutely-positioned <ScreenTitle> (top: 40%), which floats over the
 * content; and no setResonanceLayout title either — the signup flow runs the
 * layout in welcomePage mode, where the header renders no title at all.
 *
 * PERSISTENCE: like every other identity field (name/email/phone), neither
 * value is sent to the server here. They are validated and handed up to
 * HomeContent, which holds them in a dedicated variable (pendingNir) and posts
 * them to the dedicated cloisonné endpoint ONLY at consent time, right before
 * the consent itself — and, when the OPTIONAL step is left empty, withdraws a
 * NIR an earlier attempt may have stored. A NIR posted by a consent attempt
 * that then failed is never linked (the export only takes consented people) and
 * is removed server-side by the abandoned-signup sweeps, or at the latest by the
 * post-deposit purge.
 * They must never be merged into the consent payload (postConsentRequestType):
 * that path writes to the users row, which the cloisonnement forbids.
 */
import { ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import styled from "styled-components";
import Container from "../../../atoms/Container";
import { FieldError } from "../../../atoms/LoginTextField";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import Question from "../../../molecules/Question/Question";
import useVocalisation from "../../../../hooks/useVocalisation";
import { useConfig } from "../../../../contexts/ConfigContext";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import {
  getNirCollectionMode,
  isBirthdateConsistentWithNir,
  isPlausibleBirthdate,
  isValidNir,
  normalizeNir,
  shouldAskNirHolderBirthdate,
} from "../../../../helper/nir";
import {
  DateConstraintEnum,
  DateConstraintType,
  DateFormatEnum,
  DateQuestionType,
  DateSensEnum,
  DateUnitEnum,
  QuestionTextTypeEnum,
  QuestionTypeEnum,
  TextQuestionType,
} from "../../../../types/Question";

// What the step hands up to HomeContent: the normalized NIR and — only for
// roles linking the holder's own care — the holder's date de naissance.
export interface PendingNirType {
  nir: string;
  birthdate: string | null; // YYYY-MM-DD
}

// Rendered through the real questionnaire Question component so the field
// shares the exact same chrome (title, surface, IonItem input) as every other
// question in the app.
const buildNirQuestion = (title: string, description: string, placeholder: string, isMandatory: boolean): TextQuestionType => ({
  id: -1,
  title,
  // The format explanation lives in the card's description slot; the input's
  // placeholder shows the bare example (the generic "type your text here"
  // copy would be misleading for a number).
  description,
  export_code: "nir",
  integral: { is_integral: false, integral_errorcode: null },
  // Optional mode: no mandatory star — leaving it empty and pressing Next IS
  // the skip (there is no separate skip button).
  is_mandatory_question: isMandatory,
  is_active: true,
  score_coefficient: 0,
  order: 9998,
  version: "1",
  type: QuestionTypeEnum.TEXT,
  // LINE = single-line free text (Corsican NIRs contain 2A/2B, so never a
  // number input); normalizeNir strips spaces/dots/dashes on submit.
  detail: { type: QuestionTextTypeEnum.LINE, placeholder },
});

// No lower bound on the picker (DISABLED); the upper bound is TODAY (a zero-day
// relative offset is exactly the local today), so a future day cannot be picked.
// The full rule (real day, from 1900, not in the future) is enforced by the form
// and the server.
const NO_DATE_LIMIT: DateConstraintType = {
  date_type: DateConstraintEnum.DISABLED,
  todays_date_sens: null,
  todays_date_quantity: null,
  todays_date_unit: null,
  fixed_date: null,
};
const UP_TO_TODAY: DateConstraintType = {
  date_type: DateConstraintEnum.RELATIVE,
  todays_date_sens: DateSensEnum.MINUS,
  todays_date_quantity: 0,
  todays_date_unit: DateUnitEnum.DAY,
  fixed_date: null,
};

// The real questionnaire DATE component, so the picker looks exactly like every
// other date in the app. It emits a LOCAL date (YYYY-MM-DD, possibly followed by
// a local time without offset) — the first 10 characters are the picked day.
const buildBirthdateQuestion = (title: string, description: string, isMandatory: boolean): DateQuestionType => ({
  id: -2,
  title,
  description,
  export_code: "nir_holder_birthdate",
  integral: { is_integral: false, integral_errorcode: null },
  is_mandatory_question: isMandatory,
  is_active: true,
  score_coefficient: 0,
  order: 9999,
  version: "1",
  type: QuestionTypeEnum.DATE,
  detail: { format: DateFormatEnum.DAY, min_date: NO_DATE_LIMIT, max_date: UP_TO_TODAY },
});

interface DisplayNirProps {
  // The values previously entered this session (back-navigation prefill).
  initialNir: PendingNirType | null;
  // Called with the normalized NIR (+ the holder's date when asked) — or null
  // when skipped (OPTIONAL mode). HomeContent stores it and resumes the
  // stratification → sites → consent chain; the actual POST happens at consent.
  onCompleted: (pending: PendingNirType | null) => void;
}

// The picked day read back in words (« 12 mai 1985 »): the wheels start on today's
// day of the month, and the day is the one datum nothing can cross-check (the NIR
// embeds only the year and month), so the participant sees exactly what is sent.
const formatPickedDay = (value: string, language: string): string | null => {
  const isoDay = String(value).slice(0, 10);
  if (!isPlausibleBirthdate(isoDay)) return null;
  const [year, month, day] = isoDay.split("-").map(Number);
  try {
    return new Intl.DateTimeFormat(language, { dateStyle: "long", timeZone: "UTC" })
      .format(new Date(Date.UTC(year, month - 1, day)));
  } catch {
    return isoDay;
  }
};

const DisplayNir = ({ initialNir, onCompleted }: DisplayNirProps): ReactElement => {
  const { t, i18n } = useTranslation();
  const { readText } = useVocalisation();
  const { selectedRole } = useConfig();
  const { goPrevious } = useHomeContext();
  const isOptional = getNirCollectionMode(selectedRole) === "OPTIONAL";
  const asksBirthdate = shouldAskNirHolderBirthdate(selectedRole);

  const { values, touched, errors, submitForm, setFieldValue } =
    useFormik({
      initialValues: {
        nir: initialNir?.nir ?? "",
        birthdate: initialNir?.birthdate ?? "",
      },
      validationSchema: Yup.object().shape({
        // OPTIONAL mode has no separate skip button: an EMPTY field + Next IS
        // the skip. Anything typed must still pass the checksum.
        nir: isOptional
          ? Yup.string().test("nir-checksum-if-filled", "form_error_nir", (value) => !value || isValidNir(value))
          : Yup.string()
            .required("form_error_required")
            .test("nir-checksum", "form_error_nir", (value) => isValidNir(value)),
        // Asked only for roles linking the holder's own care. Required as soon as a
        // NIR is given (in OPTIONAL mode, an empty NIR skips the whole step and the
        // date is ignored — so no date rule may block that skip); must be a real past
        // day and agree with the NIR's embedded birth year/month — the same rules the
        // server enforces.
        birthdate: asksBirthdate
          ? Yup.string()
            .test("birthdate-required-with-nir", "form_error_required", function (value) {
              const { nir } = this.parent as { nir?: string };
              return !nir || Boolean(value);
            })
            .test("birthdate-plausible", "form_error_nir_birthdate_invalid", function (value) {
              const { nir } = this.parent as { nir?: string };
              return !value || !nir || isPlausibleBirthdate(String(value).slice(0, 10));
            })
            .test("birthdate-nir-consistency", "form_error_nir_birthdate_mismatch", function (value) {
              const { nir } = this.parent as { nir?: string };
              if (!value || !nir || !isValidNir(nir)) return true;
              return isBirthdateConsistentWithNir(nir, String(value).slice(0, 10));
            })
          : Yup.string(),
      }),
      onSubmit: ({ nir, birthdate }) => {
        if (!nir) {
          onCompleted(null);
          return;
        }
        onCompleted({
          nir: normalizeNir(nir),
          birthdate: asksBirthdate && birthdate ? String(birthdate).slice(0, 10) : null,
        });
      },
    });

  const pickedDay = asksBirthdate && values.birthdate
    ? formatPickedDay(values.birthdate, i18n.language)
    : null;

  return (
    <Container>
      <Content>
        <Title>
          {isOptional
            ? `${t("home_nir_title")} (${t("home_nir_optional_label")})`
            : t("home_nir_title")}
        </Title>
        <IntroCard>
          <IntroText>{t("home_nir_explanation_snds")}</IntroText>
          {asksBirthdate && (
            <IntroTextSecondary>{t("home_nir_holder_birthdate_explanation")}</IntroTextSecondary>
          )}
        </IntroCard>

        <Question
          question={buildNirQuestion(
            t("home_nir_field_label"),
            t("home_nir_placeholder"),
            t("home_nir_input_placeholder"),
            !isOptional
          )}
          answer={values.nir ?? ""}
          onAnswerChange={(value) => setFieldValue("nir", (value as string) ?? "")}
          onEnterNext={submitForm}
          readText={readText}
          cardMargin="12px 20px"
        />
        {/* Rendered as a REAL error (red, danger styling) — the shared
            QuestionDisplay ruleMessage slot shows grey description-style text
            that reads as a hint, not a blocker. */}
        {touched.nir && errors.nir && (
          <NirFieldError>{t(errors.nir as string)}</NirFieldError>
        )}

        {asksBirthdate && (
          <>
            <Question
              question={buildBirthdateQuestion(
                t("home_nir_holder_birthdate_label"),
                t("home_nir_holder_birthdate_hint"),
                !isOptional
              )}
              answer={values.birthdate ?? ""}
              onAnswerChange={(value) => setFieldValue("birthdate", (value as string) ?? "")}
              readText={readText}
              cardMargin="12px 20px"
            />
            {touched.birthdate && errors.birthdate ? (
              <NirFieldError>{t(errors.birthdate as string)}</NirFieldError>
            ) : (
              pickedDay && (
                <PickedDay aria-live="polite">
                  {t("home_nir_holder_birthdate_readback", { date: pickedDay })}
                </PickedDay>
              )
            )}
          </>
        )}
      </Content>

      <NextPrevButtons
        onPreviousClick={goPrevious}
        onNextClick={submitForm}
      />
    </Container>
  );
};

// Normal-flow content block; NextPrevButtons (margin-top: auto) stays pinned
// to the bottom of the flex Container.
const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

// In-flow title (the welcomePage layout renders no header title, and the
// absolute ScreenTitle atom overlaps content — never use it here).
const Title = styled.h1`
  margin: 18px 50px 4px;
  font-size: 20px;
  font-weight: bold;
  color: var(--app-color-text, #393939);
`;

// Flat, app-like intro surface (same margin rhythm as FormGroup: 15px 50px).
const IntroCard = styled.div`
  margin: 15px 50px 5px;
  padding: 14px 16px;
  border-radius: 14px;
  background: rgba(var(--ion-color-primary-rgb, 45, 182, 180), 0.08);
`;

const IntroText = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: var(--app-color-text, #393939);
`;

const IntroTextSecondary = styled.p`
  margin: 10px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: var(--app-color-text, #393939);
  opacity: 0.8;
`;

// Same danger styling as the field errors, aligned with the question card.
const NirFieldError = styled(FieldError)`
  margin: 6px 25px 0;
`;

// Neutral read-back under the date card (same alignment as the field errors).
const PickedDay = styled.p`
  margin: 6px 25px 0;
  font-size: 13px;
  line-height: 1.4;
  color: var(--app-color-text, #393939);
  opacity: 0.8;
`;

export default DisplayNir;
